import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const styles = {
  page: {
    background: '#fffff',
    color: '#111111',
    minHeight: '100vh',
    fontFamily: 'var(--font-body, Inter, sans-serif)',
  },
  hero: {
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '80px 40px 48px',
    textAlign: 'center',
  },
  heroEyebrow: {
    fontSize: 10,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: 'var(--gold, #c9a96e)',
    fontWeight: 600,
    marginBottom: 16,
  },
  heroTitle: {
    fontFamily: 'var(--font-display, "Cormorant Garamond", serif)',
    fontSize: 'clamp(32px, 5vw, 52px)',
    fontWeight: 300,
    letterSpacing: '0.05em',
    color: '#111111',
    marginBottom: 16,
  },
  heroDate: {
    fontSize: 12,
    color: '#111111',
    letterSpacing: '0.1em',
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '56px 24px 80px',
  },
  section: {
    marginBottom: 48,
  },
  sectionTitle: {
    fontFamily: 'var(--font-display, "Cormorant Garamond", serif)',
    fontSize: 22,
    fontWeight: 400,
    color: '#111111',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    letterSpacing: '0.05em',
  },
  para: {
    fontSize: 14,
    lineHeight: 1.9,
    color: '#111111',
    marginBottom: 14,
  },
  list: {
    paddingLeft: 20,
    marginBottom: 14,
  },
  listItem: {
    fontSize: 14,
    lineHeight: 1.9,
    color: '#111111',
    marginBottom: 8,
  },
  highlight: {
    color: 'var(--gold, #c9a96e)',
    fontWeight: 500,
  },
  contactBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderLeft: '3px solid var(--gold, #c9a96e)',
    padding: '24px 28px',
    marginTop: 48,
    borderRadius: 2,
  },
  contactTitle: {
    fontSize: 13,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#111111',
    fontWeight: 600,
    marginBottom: 12,
  },
  contactText: {
    fontSize: 13,
    color: '#111111',
    lineHeight: 1.8,
  },
  contactLink: {
    color: 'var(--gold, #c9a96e)',
    textDecoration: 'none',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#111111',
    textDecoration: 'none',
    marginBottom: 40,
    transition: 'color 0.2s',
  },
};

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms & Conditions | Aurora Luxury India';
  }, []);

  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <p style={styles.heroEyebrow}>Legal</p>
        <h1 style={styles.heroTitle}>Terms & Conditions</h1>
        <p style={styles.heroDate}>Last updated: June 2025</p>
      </div>

      <div style={styles.container}>
        <Link to="/" style={styles.backLink}
          onMouseEnter={e => e.target.style.color = '#fff'}
          onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.4)'}>
          ← Back to Home
        </Link>

        {/* 1 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Acceptance of Terms</h2>
          <p style={styles.para}>
            By accessing or using the Aurora Luxury India website at <span style={styles.highlight}>auroraluxury.shop</span> ("Site"), you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use our Site or services.
          </p>
          <p style={styles.para}>
            These terms apply to all visitors, customers, and others who access or use the Site. We reserve the right to update or modify these terms at any time without prior notice. Continued use of the Site following any changes constitutes your acceptance of the revised terms.
          </p>
        </div>

        {/* 2 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. About Aurora Luxury India</h2>
          <p style={styles.para}>
            Aurora Luxury India is a curated platform specialising in 100% authentic pre-owned and imported luxury handbags, wallets, and accessories from the world's finest luxury houses including Louis Vuitton, Chanel, Gucci, Hermès, Prada, Dior, and more.
          </p>
          <p style={styles.para}>
            We are an independent reseller. Aurora Luxury India is not affiliated with, endorsed by, or officially connected to any luxury brand mentioned on this Site. All brand names and trademarks belong to their respective owners.
          </p>
        </div>

        {/* 3 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Product Authenticity</h2>
          <p style={styles.para}>
            Every product listed on Aurora Luxury India is represented to the best of our knowledge as authentic. We source our products through trusted channels and conduct thorough authenticity checks before listing.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>All products are carefully inspected before dispatch.</li>
            <li style={styles.listItem}>Authenticity cards, dust bags, and boxes are included where available.</li>
            <li style={styles.listItem}>Condition ratings (New, Excellent, Good) are clearly stated on each product page.</li>
            <li style={styles.listItem}>If you have any concerns about a product's authenticity after receiving it, contact us within 48 hours of delivery.</li>
          </ul>
        </div>

        {/* 4 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Pricing & Payment</h2>
          <p style={styles.para}>
            All prices displayed on the Site are in Indian Rupees (INR) and are inclusive of applicable taxes unless stated otherwise. Prices are subject to change without notice.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>We accept payments via UPI, Net Banking, Credit/Debit Cards, and other methods displayed at checkout.</li>
            <li style={styles.listItem}>Payment must be completed in full before an order is confirmed and dispatched.</li>
            <li style={styles.listItem}>Aurora Luxury India does not store your payment credentials on its servers.</li>
            <li style={styles.listItem}>In case of a failed transaction, any amount debited will be refunded to the original payment method within 5–7 business days.</li>
          </ul>
        </div>

        {/* 5 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Shipping & Delivery</h2>
          <p style={styles.para}>
            We ship across India. Delivery timelines are estimates and may vary depending on your location, courier availability, and other factors.
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Standard delivery: 4–7 business days.</li>
            <li style={styles.listItem}>Express delivery: 1–3 business days (where available).</li>
            <li style={styles.listItem}>All orders are shipped with tracking and insurance coverage.</li>
            <li style={styles.listItem}>Aurora Luxury India is not responsible for delays caused by courier partners or external circumstances beyond our control.</li>
          </ul>
        </div>

        {/* 6 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Returns & Exchanges</h2>
          <p style={styles.para}>
            We want you to be completely satisfied with your purchase. Returns and exchanges are accepted under the following conditions:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Return requests must be raised within <span style={styles.highlight}>3 days</span> of receiving the product.</li>
            <li style={styles.listItem}>The product must be unused, in original condition, and with all original accessories and packaging.</li>
            <li style={styles.listItem}>Items showing signs of use, damage, or alteration after delivery will not be accepted for return.</li>
            <li style={styles.listItem}>Customised or specially sourced items are non-returnable.</li>
            <li style={styles.listItem}>Refunds, once approved, will be processed within 7–10 business days.</li>
          </ul>
        </div>

        {/* 7 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Intellectual Property</h2>
          <p style={styles.para}>
            All content on this Site including text, images, graphics, logos, and design elements are the property of Aurora Luxury India or used with permission. You may not copy, reproduce, distribute, or use any content from this Site without prior written permission.
          </p>
          <p style={styles.para}>
            Product images of luxury items belong to their respective brands and are used solely for descriptive purposes. Aurora Luxury India makes no claim of ownership over brand trademarks or imagery.
          </p>
        </div>

        {/* 8 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Limitation of Liability</h2>
          <p style={styles.para}>
            Aurora Luxury India shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Site or purchase of products. Our total liability in any matter shall not exceed the amount paid by you for the product in question.
          </p>
        </div>

        {/* 9 */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Governing Law</h2>
          <p style={styles.para}>
            These Terms & Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana, India.
          </p>
        </div>

        {/* Contact */}
        <div style={styles.contactBox}>
          <p style={styles.contactTitle}>Questions about these Terms?</p>
          <p style={styles.contactText}>
            Contact our client services team at{' '}
            <a href="mailto:support@auroraluxury.shop" style={styles.contactLink}>
              support@auroraluxury.shop
            </a>
            <br />
            or reach us via{' '}
            <a href="https://www.instagram.com/aurora_luxury_india" target="_blank" rel="noreferrer" style={styles.contactLink}>
              Instagram
            </a>
            . We typically respond within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}